import { useState, useEffect } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PaperPlaneTilt, CheckCircle, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PayForDeletionModal({ open, onOpenChange, caseData, onSent }) {
    const [form, setForm] = useState({
        recipient_email: "",
        recipient_name: "",
        recipient_role: "creditor",
        offer_amount: "",
        offer_type: "pay_for_deletion",
        payment_terms: "Single payment in full within 14 days of written acceptance via certified funds.",
        deadline_days: 14,
        custom_message: "",
    });
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!open) return;
        // Auto-suggest 30% of amount_owed as opening pay-for-deletion offer
        const suggested = caseData?.amount_owed ? Math.round(caseData.amount_owed * 0.3) : "";
        setForm((f) => ({ ...f, offer_amount: f.offer_amount || suggested }));
        // Pre-fill recipient from first contact if available
        const firstContact = (caseData?.contacts || [])[0];
        if (firstContact && !form.recipient_email) {
            setForm((f) => ({
                ...f,
                recipient_email: firstContact.email || "",
                recipient_name: firstContact.name || "",
                recipient_role: firstContact.role || "creditor",
            }));
        }
        setResult(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, caseData]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            const { data } = await api.post("/offers", {
                case_id: caseData.id,
                recipient_email: form.recipient_email,
                recipient_name: form.recipient_name,
                recipient_role: form.recipient_role,
                offer_amount: parseFloat(form.offer_amount),
                offer_type: form.offer_type,
                payment_terms: form.payment_terms,
                deadline_days: parseInt(form.deadline_days, 10) || 14,
                custom_message: form.custom_message || null,
            });
            setResult(data);
            if (data.delivery?.mocked) {
                toast.warning("Email transport not configured (RESEND_API_KEY missing). Share the public URL manually below.");
            } else if (data.delivery?.status === "sent") {
                toast.success(`Offer emailed to ${data.recipient_email}`);
            } else {
                toast.error(`Email failed: ${data.delivery?.detail || data.delivery?.status}`);
            }
            onSent?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSending(false); }
    };

    const reset = () => {
        setResult(null);
        onOpenChange(false);
    };

    const amount = parseFloat(form.offer_amount) || 0;
    const owed = caseData?.amount_owed || 0;
    const pct = owed ? (amount / owed * 100) : 0;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); else onOpenChange(true); }}>
            <DialogContent className="sm:max-w-xl" data-testid="pfd-modal">
                {!result ? (
                    <>
                        <DialogHeader>
                            <DialogTitle className="font-serif text-3xl">Send a Pay-for-Deletion offer</DialogTitle>
                            <p className="text-sm text-slate-500">Email a formal settlement offer with a one-click Accept/Decline link. Recipient lands on a branded LienLift Direct page to respond.</p>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4" data-testid="pfd-form">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="rn">Recipient name *</Label>
                                    <Input data-testid="pfd-recipient-name" id="rn" required value={form.recipient_name} onChange={set("recipient_name")} className="mt-1.5 h-10" />
                                </div>
                                <div>
                                    <Label>Role</Label>
                                    <Select value={form.recipient_role} onValueChange={(v) => setForm({ ...form, recipient_role: v })}>
                                        <SelectTrigger data-testid="pfd-recipient-role" className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="creditor">Creditor</SelectItem>
                                            <SelectItem value="attorney">Attorney</SelectItem>
                                            <SelectItem value="debtor">Debtor</SelectItem>
                                            <SelectItem value="other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="re">Recipient email *</Label>
                                <Input data-testid="pfd-recipient-email" id="re" type="email" required value={form.recipient_email} onChange={set("recipient_email")} className="mt-1.5 h-10" />
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-2">
                                    <Label htmlFor="oa">Offer amount (USD) *</Label>
                                    <Input data-testid="pfd-amount" id="oa" type="number" min="1" step="0.01" required value={form.offer_amount} onChange={set("offer_amount")} className="mt-1.5 h-10 font-mono" />
                                    {owed > 0 && amount > 0 && (
                                        <p className="text-xs text-slate-500 mt-1">{pct.toFixed(1)}% of ${owed.toLocaleString()} owed</p>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="dl">Deadline (days)</Label>
                                    <Input data-testid="pfd-deadline" id="dl" type="number" min="1" max="60" value={form.deadline_days} onChange={set("deadline_days")} className="mt-1.5 h-10 font-mono" />
                                </div>
                            </div>
                            <div>
                                <Label>Offer type</Label>
                                <Select value={form.offer_type} onValueChange={(v) => setForm({ ...form, offer_type: v })}>
                                    <SelectTrigger data-testid="pfd-type" className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="pay_for_deletion">Pay-for-Deletion (delete tradeline)</SelectItem>
                                        <SelectItem value="lump_sum">Lump-sum settlement</SelectItem>
                                        <SelectItem value="payment_plan">Payment plan</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="pt">Payment terms</Label>
                                <Textarea data-testid="pfd-terms" id="pt" rows={2} value={form.payment_terms} onChange={set("payment_terms")} className="mt-1.5" />
                            </div>
                            <div>
                                <Label htmlFor="cm">Personal note (optional)</Label>
                                <Textarea data-testid="pfd-message" id="cm" rows={3} value={form.custom_message} onChange={set("custom_message")} className="mt-1.5" placeholder="e.g. Mention hardship circumstances or context for the offer." />
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={reset}>Cancel</Button>
                                <Button type="submit" disabled={sending} data-testid="pfd-send-btn" className="bg-[#0F1E3D] hover:bg-[#1A2C57]">
                                    <PaperPlaneTilt size={14} className="mr-1.5" weight="fill" />
                                    {sending ? "Sending…" : "Send offer"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </>
                ) : (
                    <div className="space-y-4" data-testid="pfd-result">
                        <DialogHeader>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                                    <CheckCircle size={20} weight="duotone" className="text-emerald-700" />
                                </div>
                                <DialogTitle className="font-serif text-3xl">Offer created</DialogTitle>
                            </div>
                        </DialogHeader>
                        <p className="text-sm text-slate-600">
                            {result.delivery?.mocked
                                ? "Email transport isn't configured yet — copy the link below and share it via your own email or text."
                                : `An email with an Accept/Decline link has been delivered to ${result.recipient_email}.`}
                        </p>
                        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex items-center gap-2">
                            <input
                                readOnly
                                value={result.public_url}
                                data-testid="pfd-public-url"
                                className="flex-1 bg-transparent text-xs font-mono text-slate-700 outline-none"
                                onFocus={(e) => e.target.select()}
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { navigator.clipboard.writeText(result.public_url); toast.success("Link copied"); }}
                                data-testid="pfd-copy-btn"
                            >
                                <Copy size={14} className="mr-1" /> Copy
                            </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                            The recipient can accept or decline directly from that page. Your case status will auto-update when they respond.
                        </p>
                        <DialogFooter>
                            <Button onClick={reset} className="bg-[#0F1E3D] hover:bg-[#1A2C57]">Done</Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
